import { useState } from 'react';
import { logout } from '../account';
import { hardSignOut } from '../lib/localScan';
import { useAccount } from '../account/context';

/** Header account control: a small avatar circle with a dropdown (email, My scans,
 *  Sign out), or a Sign in button. Lives at the far right of the site header (#4). */
export default function AccountWidget(): JSX.Element {
  const { account, setModal } = useAccount();
  const [open, setOpen] = useState(false);

  const Avatar = (
    <span className="w-8 h-8 rounded-full border border-cyan/50 bg-cyan/10 flex items-center justify-center text-cyan shrink-0">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    </span>
  );

  if (!account) {
    return (
      <button onClick={() => setModal('signin')} title="Sign in"
        className="inline-flex items-center gap-2 font-mono text-sm text-cyan hover:opacity-80 transition">
        {Avatar}<span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }

  const tier = account.subscription.tier;
  return (
    <div className="relative">
      {/* The whole control read as decoration: no border, no background, and a
          hover that only dimmed it. Now it looks like the button it is, and the
          open state is visibly distinct from the closed one. */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={account.email}
        className={`inline-flex items-center gap-2 rounded-xl border pl-1.5 pr-2.5 py-1.5 transition ${
          open
            ? 'border-cyan/70 bg-cyan/10'
            : 'border-[#70707D] bg-warm-dark-2 hover:border-cyan/70 hover:bg-[#32323E]'
        }`}
      >
        {Avatar}
        <span className={`hidden sm:inline font-mono text-sm ${tier === 'pro' ? 'text-measured' : 'text-text-secondary'}`}>{tier === 'pro' ? 'PRO' : 'FREE'}</span>
        <span className={`font-mono text-[10px] transition-transform ${open ? 'rotate-180 text-cyan' : 'text-text-secondary'}`}>▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-64 z-50 rounded-xl border border-warm-dark-2 bg-warm-dark shadow-2xl shadow-black/50 p-2">
            <div className="px-2 py-2 border-b border-warm-dark-2 mb-2">
              <div className="text-sm text-text-primary truncate">{account.email}</div>
              <div className="font-mono text-sm text-text-secondary">{tier === 'pro' ? 'Pro plan' : 'Free plan'}</div>
            </div>

            {/* The primary action in the whole menu — everything a returning
                client came back for is behind it, so it gets the solid button
                rather than a line of text among other lines of text. */}
            <button
              onClick={() => { setOpen(false); window.location.hash = 'account'; }}
              className="w-full flex items-center justify-between gap-2 bg-cyan text-warm-dark font-medium rounded-lg px-3 py-2.5 text-sm hover:opacity-90 active:translate-y-px transition"
            >
              <span>My scans &amp; progress</span>
              <span aria-hidden="true">→</span>
            </button>

            <button
              /*
                Sign out drops the scan too.

                `refresh()` only re-reads /me, so the previous person's audit
                stayed on screen and in localStorage, and came back on reload.
                `hardSignOut` clears the stored scan and reloads onto a clean
                URL, which is the only way to be sure nothing is left mounted.
              */
              onClick={() => { setOpen(false); void logout().catch(() => {}).then(hardSignOut); }}
              className="w-full text-left mt-1.5 px-3 py-2 rounded-lg text-sm text-text-secondary border border-transparent hover:text-text-primary hover:border-[#70707D] hover:bg-warm-dark-2 transition"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}